import React, { FC, useState } from 'react';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { Typography, makeStyles, CircularProgress } from '@material-ui/core/';
import { Create } from '@material-ui/icons';
import * as Sentry from '@sentry/browser';
import { ApolloQueryResult } from 'apollo-client';

import { LONG_DATE_FORMAT } from 'src/helpers/dateHelpers';
import { NoteNode } from 'src/generated/chat_gql';

import { GetMemberInformationQuery } from './documents/GetMemberInformation.generated';
import { useCreateNoteMutation } from './documents/CreateNote.chat.generated';
import { colors } from '../../themes/colors';
import MHButton from '../MHButton';
import MHInput from '../MHInput';
import { GetMyNotesQuery } from './documents/GetMyNotes.chat.generated';

const NoteBox = ({
  content,
  createdAt,
}: {
  content: NoteNode['content'];
  createdAt: NoteNode['createdAt'];
}) => {
  const classes = useStyles();

  return (
    <div className={classes.noteHolder}>
      <div className={classes.noteHeader}>
        <Typography className={classes.noteDateFormat}>
          {moment(createdAt).format(LONG_DATE_FORMAT)}
        </Typography>
      </div>
      <div>
        <Typography className={classes.noteContentFormat}>{content}</Typography>
      </div>
    </div>
  );
};

type Props = {
  notes: GetMyNotesQuery['getMyNotes'];
  isGettingNotes: boolean;
  member: NonNullable<GetMemberInformationQuery['member']>;
  refetchNotes: () => Promise<ApolloQueryResult<GetMyNotesQuery>>;
};

export const MemberSettingsNotes: FC<Props> = ({ notes, member, isGettingNotes, refetchNotes }) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [showCreateNote, setShowCreateNote] = useState(false);
  const [tempNote, updateTempNote] = useState('');
  const [createNote, { loading: isCreatingNote }] = useCreateNoteMutation();

  const onCreateNewNote = () => {
    setShowCreateNote(true);
  };

  const handleNoteInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateTempNote(event.target.value);
  };

  const cancelNewNote = () => {
    updateTempNote('');
    setShowCreateNote(false);
  };

  const saveNewNote = async () => {
    try {
      const variables = {
        content: tempNote,
        memberUuid: member.uuid,
      };

      const res = await createNote({ variables });

      if (!res.data?.createNote) {
        throw new Error('createNote data is not present in the response');
      }
      refetchNotes();

      enqueueSnackbar('Save success!', { variant: 'success' });
      updateTempNote('');
      setShowCreateNote(false);
    } catch (error) {
      enqueueSnackbar('Something went wrong saving info.', { variant: 'error' });
      Sentry.captureException(error);
    }
  };

  if (showCreateNote) {
    return (
      <div>
        <div className={classes.header}>
          <Typography className={classes.newNoteHeader}>New Note</Typography>
        </div>
        <MHInput value={tempNote} onChange={handleNoteInputChange} multiline rows={5} />
        <div className={classes.newNoteButtonRow}>
          <MHButton onClick={cancelNewNote} variant="outlined" disabled={isCreatingNote}>
            <Typography className={classes.cancelNewNoteText}>Cancel</Typography>
          </MHButton>
          <MHButton
            onClick={saveNewNote}
            color="primary"
            disabled={!tempNote}
            loading={isCreatingNote}
          >
            <Typography>Save Note</Typography>
          </MHButton>
        </div>
      </div>
    );
  }

  return (
    <>
      {isGettingNotes && (
        <div className={classes.loaderContainer}>
          <CircularProgress size={30} />
        </div>
      )}
      <div>
        {notes?.map((note) => {
          if (!note) return null;
          return <NoteBox key={note.id} createdAt={note.createdAt} content={note.content} />;
        })}
      </div>
      <MHButton
        onClick={onCreateNewNote}
        buttonWrapperExtraClasses={[classes.createNoteButtonWrapper]}
        extraClasses={[classes.createNoteButton]}
      >
        <Create />
        <Typography className={classes.createNoteButtonText}>Write a Note</Typography>
      </MHButton>
    </>
  );
};

const useStyles = makeStyles({
  header: {
    backgroundColor: colors.gray10,
    minHeight: 48,
  },
  tabText: {
    fontFamily: 'Avenir-Medium',
    color: colors.gray11,
    fontSize: 14,
  },
  tabTextSelected: {
    fontFamily: 'Avenir-Heavy',
    color: colors.black5,
  },
  newNoteHeader: {
    fontFamily: 'Avenir-Heavy',
    fontSize: 16,
    paddingTop: 12,
    paddingBottom: 12,
    paddingLeft: 12,
  },
  noteHolder: {
    display: 'flex',
    flexDirection: 'column',
    padding: '10px 10px 10px 5px',
    borderBottom: `1px solid ${colors.gray1}`,
  },
  noteHeader: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  noteDateFormat: {
    fontFamily: 'Avenir-Medium',
    fontSize: 14,
    fontWeight: 900,
    color: colors.black6,
  },
  noteContentFormat: {
    fontFamily: 'Avenir-Book',
    fontSize: 14,
    color: colors.black1,
    marginTop: 10,
    paddingLeft: 20,
    borderLeft: `1px solid ${colors.gray1}`,
  },
  createNoteButtonWrapper: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: 10,
  },
  createNoteButton: {
    width: 'inherit',
  },
  createNoteButtonText: {
    fontFamily: 'Avenir-Heavy',
    fontSize: 14,
    color: '#ffffff',
    marginLeft: 10,
  },
  newNoteButtonRow: {
    display: 'flex',
    justifyContent: 'space-around',
    marginTop: 15,
  },
  cancelNewNoteText: {
    fontFamily: 'Avenir-Heavy',
    color: colors.gray5,
  },
  loaderContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
});
